import React, { useState, useCallback } from 'react';
import { Box, Typography, IconButton, Tooltip, TypographyProps } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopyOutlined';
import CheckIcon from '@mui/icons-material/Check';

export interface CopyableTextProps {
  /** The full value to display & copy. */
  value?: string | number | null;
  /** Optional display override (e.g. shortened middle-truncation). */
  display?: React.ReactNode;
  /** Truncate the visible text with ellipsis and cap its width. */
  truncate?: boolean;
  /** Max width when truncating. Default 180. */
  maxWidth?: number | string;
  /** Show the value inside a subtle "code" chip (good for IDs / hashes). */
  mono?: boolean;
  /** Typography variant for the text. Default 'body2'. */
  variant?: TypographyProps['variant'];
  /** Typography color. */
  color?: TypographyProps['color'];
  /** Hide the copy button (text-only with tooltip). */
  hideCopy?: boolean;
  /** Fallback shown when value is empty. Default '-'. */
  placeholder?: React.ReactNode;
  /** Extra sx on the wrapper. */
  sx?: any;
}

/**
 * Displays a (optionally truncated) value with a copy-to-clipboard button and a
 * tooltip revealing the full text. Ideal for IDs, hashes, wallet addresses,
 * emails, order numbers and other long technical values on dense/mobile UIs.
 */
const CopyableText: React.FC<CopyableTextProps> = ({
  value,
  display,
  truncate = true,
  maxWidth = 180,
  mono = false,
  variant = 'body2',
  color,
  hideCopy = false,
  placeholder = '-',
  sx,
}) => {
  const [copied, setCopied] = useState(false);
  const text = value === null || value === undefined || value === '' ? '' : String(value);

  const handleCopy = useCallback(
    async (e: React.MouseEvent) => {
      e.stopPropagation();
      if (!text) return;
      try {
        await navigator.clipboard.writeText(text);
      } catch {
        // Fallback for non-secure contexts.
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        try {
          document.execCommand('copy');
        } catch {
          /* noop */
        }
        document.body.removeChild(ta);
      }
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1400);
    },
    [text]
  );

  if (!text) {
    return (
      <Typography variant={variant} color={color ?? 'text.secondary'} component="span">
        {placeholder}
      </Typography>
    );
  }

  const textNode = (
    <Typography
      variant={variant}
      color={color}
      component="span"
      dir="ltr"
      sx={{
        ...(truncate && {
          maxWidth,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }),
        ...(mono && {
          fontFamily: 'monospace',
          fontSize: '0.8125rem',
          px: 0.75,
          py: 0.25,
          borderRadius: 1,
          bgcolor: 'action.hover',
          unicodeBidi: 'plaintext',
        }),
        display: 'inline-block',
        verticalAlign: 'middle',
      }}
    >
      {display ?? text}
    </Typography>
  );

  return (
    <Box
      component="span"
      sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25, maxWidth: '100%', ...sx }}
    >
      <Tooltip title={text} placement="top" arrow enterTouchDelay={0}>
        {textNode}
      </Tooltip>
      {!hideCopy && (
        <Tooltip title={copied ? 'کپی شد' : 'کپی'} placement="top" arrow>
          <IconButton
            onClick={handleCopy}
            size="small"
            aria-label="کپی"
            sx={{ p: 0.375, color: copied ? 'success.main' : 'text.secondary', flexShrink: 0 }}
          >
            {copied ? (
              <CheckIcon sx={{ fontSize: 15 }} />
            ) : (
              <ContentCopyIcon sx={{ fontSize: 15 }} />
            )}
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
};

export default CopyableText;
